import { Client, Room, getStateCallbacks } from '@colyseus/sdk';
import { MapSchema } from '@colyseus/schema';
import { getGameServerPath } from '@/ApiPaths';
import { useEffect, useState } from 'react';

type BaseColumn<T> = {
  name: string;
  properties: T;
};
export type Column = BaseColumn<Map<string, string> | Record<string, string>>;
export type ColyseusColumn = BaseColumn<MapSchema<string, string>>;

type BaseRow<T> = {
  pNetId: string;
  data: T;
};
export type Row = BaseRow<Map<string, string> | Record<string, string>>;
export type ColyseusRow = BaseRow<MapSchema<string, string>>;

type BaseTable<C, R> = {
  _id: string;
  name: string;
  columns: C;
  rows: R;
};
export type Table = BaseTable<Map<string, Column>, Map<string, Row>>;
export type ColyseusTable = BaseTable<MapSchema<ColyseusColumn>, MapSchema<ColyseusRow>>;

export const client = new Client(getGameServerPath(), {
  fetchFn: (url, init) => fetch(url, { ...init, credentials: 'omit' }),
});

// TODO offer subscription api for rowdata and columnproerty changes
export const enum ChangeType {
  Add = 'ADD',
  Remove = 'REMOVE',
  Change = 'CHANGE',
}

export class TableRoom {
  public readonly room: Room;
  constructor(room: Room) {
    this.room = room;
  }

  getRows(): MapSchema<ColyseusRow> {
    return this.room.state.rows;
  }

  onRowAdd(callback: (row: ColyseusRow, pNetId: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.rows).onAdd(callback);
  }

  onRowRemove(callback: (row: ColyseusRow, pNetId: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.rows).onRemove(callback);
  }

  onRowsChange(callback: (row: ColyseusRow, pNetId: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.rows).onChange(callback);
  }

  addRow(row: Row) {
    this.room.send('addRow', { pNetId: row.pNetId, data: new MapSchema(row.data) });
  }

  removeRow(pNetId: string) {
    this.room.send('removeRow', pNetId);
  }

  changeRowData(row: Row) {
    this.room.send('changeRowData', { pNetId: row.pNetId, data: new MapSchema(row.data) });
  }

  removeRowData(pNetId: string, key: string) {
    this.room.send('removeRowData', { pNetId: pNetId, key: key });
  }

  getColumns(): MapSchema<ColyseusColumn> {
    return this.room.state.columns;
  }

  onColumnAdd(callback: (column: ColyseusColumn, name: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.columns).onAdd(callback);
  }

  onColumnsChange(callback: (column: ColyseusColumn, name: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.columns).onChange(callback);
  }

  onColumnRemove(callback: (columns: ColyseusColumn, name: string) => void) {
    const $ = getStateCallbacks(this.room);
    $(this.room.state.columns).onRemove(callback);
  }

  addColumn(column: Column) {
    this.room.send('addColumn', {
      name: column.name,
      properties: new MapSchema(column.properties),
    });
  }

  removeColumn(name: string) {
    this.room.send('removeColumn', name);
  }

  changeColumnProperties(column: Column) {
    this.room.send('changeColumnProperties', {
      name: column.name,
      properties: new MapSchema(column.properties),
    });
  }

  removeColumnProperty(name: string, propertyKey: string) {
    this.room.send('removeColumnProperty', { name, key: propertyKey });
  }

  sendChatMessage(message: string) {
    this.room.send('chatMessage', message);
  }

  onChatMessage(callback: ({ clientId, message }: { clientId: string; message: string }) => void) {
    this.room.onMessage('chatMessage', callback);
  }

  leave() {
    this.room.leave(true);
  }
}

export function useTableRoom(roomId: string): TableRoom | undefined {
  const [tableRoom, setTableRoom] = useState<TableRoom | undefined>(undefined);

  useEffect(() => {
    client.joinOrCreate(roomId).then((room) => setTableRoom(new TableRoom(room)));

    return () => {
      tableRoom?.leave();
    };
  }, [roomId]);

  return tableRoom;
}

export async function connectTableRoom(roomId: string): Promise<TableRoom> {
  return new TableRoom(await client.joinOrCreate(roomId));
}
